import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRequest } from "../hooks/useRequest";
import { getPlans, Plan } from "../api/subscription";
import ContactModal from "../components/ContactModal";
import ConsoleHeader from "../layout/ConsoleHeader";
import ashford from "@/assets/images/ashford.png";
import footLocker from "@/assets/images/foot-locker.png";
import jomashop from "@/assets/images/jomashop.png";
import michaelKors from "@/assets/images/michael-kors.png";
import nordstromRack from "@/assets/images/nordstrom-rack.png";
import saksFifthAvenue from "@/assets/images/saks-fifth-avenue.png";
import shoebacca from "@/assets/images/shoebacca.png";
import urbanOutfitters from "@/assets/images/urban-outfitters.png";
import walgreens from "@/assets/images/walgreens.png";
import yoox from "@/assets/images/yoox.png";
import zappos from "@/assets/images/zappos.png";
import "./Pricing.scss";

/** 支持的商家（logo 墙，顺序与交互稿一致） */
const MERCHANTS: { name: string; logo: string }[] = [
  { name: "Ashford", logo: ashford },
  { name: "Foot Locker", logo: footLocker },
  { name: "Jomashop", logo: jomashop },
  { name: "Michael Kors", logo: michaelKors },
  { name: "Nordstrom Rack", logo: nordstromRack },
  { name: "Saks Fifth Avenue", logo: saksFifthAvenue },
  { name: "SHOEBACCA", logo: shoebacca },
  { name: "Urban Outfitters", logo: urbanOutfitters },
  { name: "Walgreens", logo: walgreens },
  { name: "YOOX", logo: yoox },
  { name: "Zappos", logo: zappos },
];

/**
 * 商家 logo 墙的 JSON-LD（schema.org ItemList）。
 * 让爬虫理解：这是 CloudStore AI「支持的商家」的 logo 列表，
 * 每项是一个 Organization（商家）及其 logo 图片。
 */
function buildMerchantsJsonLd(): string {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const data = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Supported merchants",
    description:
      "Logos of the premium US & EU merchants supported by CloudStore AI, all accessible through a single API.",
    numberOfItems: MERCHANTS.length,
    itemListElement: MERCHANTS.map((m, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Organization",
        name: m.name,
        logo: `${origin}${m.logo}`,
        description: `${m.name} is a merchant supported by CloudStore AI.`,
      },
    })),
  };
  return JSON.stringify(data);
}

/** 主推套餐（仅 UI，接口无此字段） */
const POPULAR = new Set(["DEVELOPER"]);

interface PlanCard {
  planCode: string;
  name: string;
  price: string;
  features: string[];
  popular: boolean;
  cta: "start" | "contact";
}

const ENTERPRISE: PlanCard = {
  planCode: "ENTERPRISE",
  name: "Enterprise Plan",
  price: "Custom pricing",
  features: [
    "Custom order & API call volume",
    "Volume discounts",
    "Dedicated account manager",
    "Priority support",
    "Custom integrations & onboarding",
  ],
  popular: false,
  cta: "contact",
};

function priceLabel(feeCents: number): string {
  if (feeCents <= 0) return "Free";
  return `$${(feeCents / 100).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })} / month`;
}

/** 美分 → 美元字符串，去掉多余的 0（200 → "$2"，5 → "$0.05"） */
function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}`;
}

/** 从 apiPricing 中取指定 apiCode 的单价（美分） */
function apiPriceCents(plan: Plan, apiCode: string): number | undefined {
  return plan.apiPricing?.find((p) => p.apiCode === apiCode)?.priceCents;
}

/** 各套餐写死的附加文案（接口未提供营销文案） */
const PLAN_EXTRA_FEATURES: Record<string, string[]> = {
  FREE: ["Email support"],
  DEVELOPER: [
    "Third-party platform costs with no markup",
    "Includes 24/7 email support",
  ],
};

function features(plan: Plan): string[] {
  const list: string[] = [
    `${dollars(plan.monthlyCreditsCents)} per month free credits`,
  ];
  const orderCents = apiPriceCents(plan, "checkout.order");
  if (orderCents != null) list.push(`${dollars(orderCents)} per order placed`);
  const fetchCents = apiPriceCents(plan, "product.detail");
  if (fetchCents != null) list.push(`${dollars(fetchCents)} per product fetch`);
  list.push(...(PLAN_EXTRA_FEATURES[plan.planCode] ?? []));
  return list;
}

/** Shopify App 链接 */
const SHOPIFY_APP_URL =
  "https://apps.shopify.com/cloud-store?st_source=autocomplete&surface_detail=autocomplete_apps";

/** FAQ 列表：问题 + 答案（答案可为多段/含列表或链接） */
const FAQS: { q: string; a: JSX.Element }[] = [
  {
    q: "What are the technical advantages of your API?",
    a: (
      <>
        <p className="cs-pricing__faq-p">
          Our API is the industry's only unified solution for global luxury
          fashion catalog ingestion and order placement.
        </p>
        <ul className="cs-pricing__faq-ul">
          <li>
            Unified Endpoint Suite: Supports both GET /catalog and POST /orders
            for major EU/US luxury brands.
          </li>
          <li>
            Production-Tested Scale: 10+ years of uptime handling 5M+ successful
            order payloads.
          </li>
          <li>
            Real-Time Data Sync: Low-latency synchronization for price and
            inventory, with coupon/promo logic computed directly into the
            markdown payload.
          </li>
          <li>
            Enterprise Reliability: High-availability, production-ready
            architecture
          </li>
        </ul>
      </>
    ),
  },
  {
    q: "Do you provide product and ordering APIs for categories other than fashion?",
    a: (
      <p className="cs-pricing__faq-p">
        Yes, we do. We support a wide range of product categories beyond
        fashion. Please contact our Sales Team directly to discuss your specific
        requirements and get access to the relevant APIs.
      </p>
    ),
  },
  {
    q: "What merchants and brands do you work with?",
    a: (
      <p className="cs-pricing__faq-p">
        Our network includes 400+ premium merchants and brands across the US and
        Europe, including major department stores like Macy's, Saks Fifth
        Avenue, and Harrods, as well as leading fashion and beauty brands such as
        Michael Kors, Zappos, and many more. We provide access to over 20 million
        SKUs across fashion, beauty, and luxury categories, with product
        information, pricing, and inventory refreshed daily.
      </p>
    ),
  },
  {
    q: "How does pricing work across different plans?",
    a: (
      <>
        <p className="cs-pricing__faq-p">
          The Free Plan gives you $2/month in credits with access to 3 core API
          endpoints (products, discover, checkout).
        </p>
        <p className="cs-pricing__faq-p">
          The Developer Plan at $99/month includes $50/month in credits at 5x
          lower per-call pricing, plus access to all endpoints.
        </p>
        <p className="cs-pricing__faq-p">
          The Enterprise Plan offers fully custom pricing with volume discounts,
          a dedicated account manager, and an allocated Forward-Deployed
          Engineer.
        </p>
      </>
    ),
  },
  {
    q: "Can I start with the Free Plan and upgrade later?",
    a: (
      <p className="cs-pricing__faq-p">
        Absolutely. You can upgrade from Free to Developer at any time — your
        upgrade takes effect immediately after payment, with a new billing cycle
        starting from your upgrade date and full monthly credits. Downgrades take
        effect at the end of your current billing cycle. Your integrations
        continue working without any changes throughout the process.
      </p>
    ),
  },
  {
    q: "How long does integration take?",
    a: (
      <p className="cs-pricing__faq-p">
        Most integrations are completed in 7 to 14 days. The process is
        straightforward: we connect to your existing platform (Shopify,
        WooCommerce, custom catalog, or any REST-compatible system), our team
        handles the API configuration, and you go live with access to our entire
        merchant network. Developer and Enterprise plan clients receive dedicated
        onboarding support to accelerate the process.
      </p>
    ),
  },
  {
    q: "I'm a Shopify user, is there a simpler solution?",
    a: (
      <p className="cs-pricing__faq-p">
        You can use our Shopify App for a simpler, one-click integration.{" "}
        <a
          className="cs-pricing__faq-link"
          href={SHOPIFY_APP_URL}
          target="_blank"
          rel="noreferrer"
        >
          Click here
        </a>{" "}
        to go.
      </p>
    ),
  },
];

/**
 * 读取预渲染注入的套餐引导数据（scripts/prerender-pricing.js 写入 window.__PRICING_PLANS__）。
 * 有则用作首帧初始数据，使客户端首帧与预渲染 HTML 一致、可安全 hydrate。
 */
function getInjectedPlans(): Plan[] | null {
  if (typeof window === "undefined") return null;
  const data = (window as unknown as { __PRICING_PLANS__?: Plan[] })
    .__PRICING_PLANS__;
  return Array.isArray(data) ? data : null;
}

const Pricing = () => {
  const navigate = useNavigate();
  const plans = useRequest(getPlans, [], { initialData: getInjectedPlans() });
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    document.title = "Pricing | CloudStore AI";
  }, []);

  const openContact = () => setContactOpen(true);

  const cards: PlanCard[] = [
    ...(plans.data || []).map((p) => ({
      planCode: p.planCode,
      name: p.planName,
      price: priceLabel(p.monthlyFeeCents),
      features: features(p),
      popular: POPULAR.has(p.planCode),
      cta: "start" as const,
    })),
    ENTERPRISE,
  ];

  return (
    <div className="cs-pricing">
      {/* 顶部导航：与控制台共用同一个 Header */}
      <ConsoleHeader />

      {/* Hero */}
      <section className="cs-pricing__hero">
        <span className="cs-pricing__badge">• Flexible Plans for Every Scale</span>
        <h1 className="cs-pricing__hero-title">
          Plans that scale with your business
        </h1>
        <p className="cs-pricing__hero-sub">
          From your first API call to billions in GMV — choose the plan that
          fits where you are, and grow with us.
        </p>
      </section>

      {/* 套餐 */}
      <section className="cs-pricing__plans-section">
        <h2 className="cs-pricing__section-title">Choose your plan</h2>
        <p className="cs-pricing__section-sub">
          All plans include access to our global fashion supply network. Start
          free and scale as you grow.
        </p>

        {plans.loading && <div className="cs-pricing__muted">Loading…</div>}
        {!plans.loading && plans.error && (
          <div className="cs-pricing__muted">{plans.error}</div>
        )}

        {!plans.loading && !plans.error && (
          <div className="cs-pricing__plans">
            {cards.map((card) => (
              <div
                key={card.planCode}
                className={`cs-pricing__plan${
                  card.popular ? " cs-pricing__plan--popular" : ""
                }`}
              >
                {card.popular && (
                  <span className="cs-pricing__plan-badge">Most Popular</span>
                )}
                <div className="cs-pricing__plan-name">{card.name}</div>
                <div className="cs-pricing__plan-price">{card.price}</div>
                <ul className="cs-pricing__features">
                  {card.features.map((f, i) => (
                    <li key={i} className="cs-pricing__feature">
                      <span className="cs-pricing__check">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {card.cta === "contact" ? (
                  <button
                    type="button"
                    className="cs-pricing__plan-btn"
                    onClick={openContact}
                  >
                    Contact Sales
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`cs-pricing__plan-btn${
                      card.popular ? " cs-pricing__plan-btn--primary" : ""
                    }`}
                    onClick={() => navigate("/dashboard")}
                  >
                    Get Started
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Supported merchants —— logo 墙（plans 与 FAQ 之间，白色背景） */}
      <section className="cs-pricing__merchants">
        {/* JSON-LD：向爬虫声明这是「支持的商家」的 logo 列表 */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: buildMerchantsJsonLd() }}
        />
        <h2 className="cs-pricing__section-title">Supported merchants</h2>
        <p className="cs-pricing__section-sub">
          11 premium US &amp; EU retailers, available through a single API
        </p>
        <div className="cs-pricing__merchant-grid">
          {MERCHANTS.map((m) => (
            <div className="cs-pricing__merchant-cell" key={m.name}>
              <img
                className="cs-pricing__merchant-logo"
                src={m.logo}
                alt={`${m.name} logo — a merchant supported by CloudStore AI`}
                title={`${m.name} — supported merchant logo`}
                loading="lazy"
                width={120}
                height={40}
              />
            </div>
          ))}
          <div className="cs-pricing__merchant-cell cs-pricing__merchant-cell--more">
            <span className="cs-pricing__merchant-more">more</span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="cs-pricing__faq">
        <div className="cs-pricing__faq-inner">
          <h2 className="cs-pricing__section-title">
            Frequently asked questions
          </h2>
          <p className="cs-pricing__section-sub">
            Everything you need to know about our plans and services.
          </p>
          <div className="cs-pricing__faq-list">
            {FAQS.map((faq, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="cs-pricing__faq-item">
                  <button
                    type="button"
                    className="cs-pricing__faq-q"
                    onClick={() => setOpenFaq(open ? null : i)}
                  >
                    <span>{faq.q}</span>
                    <span className="cs-pricing__faq-toggle">
                      {open ? "−" : "+"}
                    </span>
                  </button>
                  {open && <div className="cs-pricing__faq-a">{faq.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 底部 CTA */}
      <section className="cs-pricing__cta">
        <h2 className="cs-pricing__cta-title">
          Ready to scale your fashion business?
        </h2>
        <p className="cs-pricing__cta-sub">
          Join 500+ e-commerce merchants and brands already powered by
          CloudStore AI.
        </p>
        <button
          type="button"
          className="cs-pricing__cta-btn"
          onClick={openContact}
        >
          Contact Us
        </button>
      </section>

      {/* Contact Us 弹窗 */}
      <ContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
      />
    </div>
  );
};

export default Pricing;
